import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Users,
  Clock,
  Settings,
  BarChart3,
  Calendar,
  Zap,
  ThumbsUp,
  ShieldCheck,
  User,
  Star,
  TrendingUp,
  Layers,
  FileText,
  Send,
  RefreshCw,
  BarChart2,
  ArrowRight,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Footer from "../components/Footer";

const CASE_SUMMARY = [
  {
    icon: Building2,
    title: "Industry",
    desc: "Healthcare (Patient Experience)",
  },
  {
    icon: Users,
    title: "Key Users",
    desc: "Front-line staff, Clinic Managers, Care Team Leads, Patient Experience Officers, IT Teams",
  },
  {
    icon: Clock,
    title: "Business Need",
    desc: "More personalized and informed patient interactions",
  },
  {
    icon: Settings,
    title: "Solution",
    desc: "Simpel Patient Experience Copilot",
  },
  {
    icon: BarChart3,
    title: "Impact",
    desc: "Higher patient satisfaction and faster response time",
  },
  {
    icon: Calendar,
    title: "Timeframe",
    desc: "10-16 months payback",
  },
];


const WHO_BENEFITS_DATA = [
  {
    role: "Chief Experience / Patient Experience Officer",
    improves:
      "Higher consistency of experience across touchpoints. Teams share the same quality of information and personalization; satisfaction and feedback scores on the pilot scope improve and stay improved.",
  },
  {
    role: "Service Line / Clinic Manager",
    improves:
      "Smoother patient conversations and fewer repeated questions. Staff access the right context quickly; wait and call handling feel more assured as a normal way of working.",
  },
  {
    role: "Front-line staff (Patient Access, Call Centre, Ward / Clinic)",
    improves:
      "Relevant information at hand during the interaction. More confidence, less time switching systems; patients receive clearer, more personal answers every time.",
  },
  {
    role: "Clinical / Care Team Lead",
    improves:
      "Aligned messaging with care pathways. Non-clinical and clinical touchpoints reinforce the same understanding of the patient's journey.",
  },
  {
    role: "CIO / IT / Information Security",
    improves:
      "Controlled use of patient and operational information. Role-based access, privacy by design and integration with approved systems.",
  },
];

const RESULTS_DATA = [
  {
    icon: Star,
    subtitle: "Patient satisfaction / experience score (pilot touchpoints)",
    title: "+5 to +12 points (scale-dependent)",
  },
  {
    icon: Clock,
    subtitle: "Time to surface relevant patient / pathway context during interaction",
    title: "40% to 60% faster",
  },
  {
    icon: TrendingUp,
    subtitle: "First-contact resolution / fewer repeat contacts for the same question",
    title: "15% to 30% improvement",
  },
  {
    icon: Users,
    subtitle: "Staff capacity redirected to higher-value work",
    title: "Clear gain on pilot teams",
  },
  {
    icon: Calendar,
    subtitle: "Typical payback window",
    title: "10 - 16 months",
  },
  {
    icon: Layers,
    subtitle: "Annual value (experience + efficiency of interactions)",
    title: "$169k - $420k (scale-dependent)",
  },
];


const PROCESS_STEPS_PATIENT = [
  {
    id: "understand",
    title: "Understand",
    desc: "Access patient context and care journey",
    icon: User,
    bgGradient: "bg-gradient-to-br from-[#93C5FD] to-[#3B82F6]",
    glow: "shadow-[0_0_24px_rgba(59,130,246,0.35)]",
  },
  {
    id: "insights",
    title: "Get insights",
    desc: "AI surfaces relevant information and guidance",
    icon: FileText,
    bgGradient: "bg-gradient-to-br from-[#E9D5FF] to-[#A855F7]",
    glow: "shadow-[0_0_24px_rgba(168,85,247,0.35)]",
  },
  {
    id: "respond",
    title: "Respond",
    desc: "Use approved responses and explanations",
    icon: Send,
    bgGradient: "bg-gradient-to-br from-[#FEF08A] to-[#EAB308]",
    glow: "shadow-[0_0_24px_rgba(234,179,8,0.35)]",
  },
  {
    id: "follow-up",
    title: "Follow up",
    desc: "Automate routine tasks and updates",
    icon: RefreshCw,
    bgGradient: "bg-gradient-to-br from-[#BBF7D0] to-[#22C55E]",
    glow: "shadow-[0_0_24px_rgba(34,197,94,0.35)]",
  },
  {
    id: "improve",
    title: "Improve",
    desc: "Track feedback and continuously enhance interactions",
    icon: BarChart2,
    bgGradient: "bg-gradient-to-br from-[#FBCFE8] to-[#EC4899]",
    glow: "shadow-[0_0_24px_rgba(236,72,153,0.35)]",
  },
];

const PatientExperienceCopilot = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/ai-opportunities?tab=Industries");
    }
  };

  // Scroll tracking across sticky hero track
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const overlayY = useTransform(
    scrollYProgress,
    [0, 0.22, 0.75, 0.98],
    ["100%", "0%", "0%", "-100%"]
  );

  const overlayOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.22, 0.75, 0.95, 1],
    [0, 0.6, 1, 1, 0.7, 0]
  );

  const handleScrollDown = () => {
    const el = document.getElementById("case-at-a-glance");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (containerRef.current) {
      const top = containerRef.current.offsetTop;
      const height = containerRef.current.offsetHeight;
      window.scrollTo({
        top: top + height,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#000000] flex flex-col text-white">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 w-full bg-[#000000] border-b border-white/[0.06] shadow-sm">
        <div className="mx-auto flex h-[76px] sm:h-[82px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
          {/* Simpel Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
          >
            <img
              src="/images/simpel-icon-mark.png"
              alt="Simpel"
              className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
                Simpel
              </span>
              <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
                AUTOMATION SIMPLIFIED
              </span>
            </div>
          </Link>

          {/* Right Breadcrumb & Back Button */}
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="hidden sm:block border-b-2 border-[#2F6DF6] pb-0.5">
              <span className="text-[14px] sm:text-[15px] font-medium text-[#2F6DF6] tracking-wide select-none">
                Industries / Healthcare
              </span>
            </div>

            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#00D2EE] px-5 py-2 sm:px-6 sm:py-2.5 text-[13px] sm:text-[14px] font-bold text-black transition-all duration-200 hover:brightness-105 hover:shadow-[0_4px_16px_rgba(0,210,238,0.35)] active:scale-95 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
              <span>Back to Cognitive AI</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full bg-[#000000]">
        {/* =========================================================================
            Hero Section with Scroll Pinning Animation (matching Screenshot 1)
            Background: /images/insights/patient-experience-hero.jpg
           ========================================================================= */}
        <section ref={containerRef} className="relative w-full h-[270vh]">
          {/* Sticky Viewport Stage */}
          <div className="sticky top-[76px] sm:top-[82px] h-[calc(100vh-76px)] sm:h-[calc(100vh-82px)] w-full overflow-hidden bg-[#000000]">
            {/* Underlying Hero Section Photo - 100% crystal clear at scroll 0 */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="/images/insights/patient-experience-hero.jpg"
                alt="Patient Experience Copilot - Healthcare Professional with Patient Summary"
                className="w-full h-full object-cover object-center block select-none"
              />
            </div>

            {/* Frosted Blur Overlay Section with Pinning Animation */}
            <motion.div
              style={{
                y: overlayY,
                opacity: overlayOpacity,
              }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 text-center"
            >
              {/* Frosted Glass Background Tint matching Screenshot 1 */}
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: "rgba(15, 27, 41, 0.94)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              />

              {/* Foreground Content */}
              <div className="relative z-10 max-w-[1240px] flex flex-col items-center pt-4 sm:pt-0">
                {/* Category Tag matching Screenshot 1 */}
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[3px] text-white/70 mb-5 sm:mb-6">
                  HEALTHCARE - PATIENT EXPERIENCE
                </p>

                {/* Main Heading matching Screenshot 1 (Strictly 2 lines) */}
                <h1 className="text-[28px] sm:text-[38px] md:text-[46px] lg:text-[50px] xl:text-[54px] font-bold leading-[1.2] sm:leading-[1.18] text-white tracking-tight">
                  <span className="block whitespace-nowrap">
                    Patient Experience Copilot:{" "}
                    <span className="bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                      Every interaction
                    </span>
                  </span>
                  <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent whitespace-nowrap">
                    feels informed and personal
                  </span>
                </h1>

                {/* Subtitle Paragraph (3 Lines matching Screenshot 1) */}
                <div className="mt-5 sm:mt-6 max-w-[850px] text-[13.5px] sm:text-[14.5px] md:text-[15px] leading-[24px] sm:leading-[26px] text-white/80 text-center">
                  <p className="whitespace-normal sm:whitespace-nowrap">
                    How Simpel Patient Experience Copilot helps teams understand patient needs,
                  </p>
                  <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                    access relevant information and support more personalized interactions —
                  </p>
                  <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                    so patients feel known and care feels connected.
                  </p>
                </div>

                {/* 4 Feature Pills in a horizontal row matching Screenshot 1 */}
                <div className="mt-8 sm:mt-10 flex flex-wrap items-center justify-center gap-6 sm:gap-10 max-w-[980px]">
                  {/* Pill 1 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <User className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Personalized patient<br className="hidden sm:inline" /> interactions
                    </span>
                  </div>

                  {/* Pill 2 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Zap className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Faster access to<br className="hidden sm:inline" /> patient context
                    </span>
                  </div>

                  {/* Pill 3 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <ThumbsUp className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Higher patient<br className="hidden sm:inline" /> satisfaction
                    </span>
                  </div>

                  {/* Pill 4 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <ShieldCheck className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Consistent and trusted<br className="hidden sm:inline" /> communication
                    </span>
                  </div>
                </div>

                {/* Vertical Dashed Line with Downward Arrow */}
                <div
                  onClick={handleScrollDown}
                  className="mt-10 sm:mt-12 flex flex-col items-center cursor-pointer group"
                  title="Scroll to explore"
                >
                  <svg
                    width="10"
                    height="120"
                    viewBox="0 0 10 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="opacity-75 transition-opacity group-hover:opacity-100"
                  >
                    <line
                      x1="5"
                      y1="0"
                      x2="5"
                      y2="110"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                    <path
                      d="M5 110L10 102M5 110L0 102"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* =========================================================================
            1. Case at a glance Section (matching Screenshot 3)
            Background: rgba(245, 250, 254, 1)
            6 Overview Cards (2 rows x 3 cols)
           ========================================================================= */}
        <section
          id="case-at-a-glance"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Case at a glance
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* 6 Cards Grid (3 cols x 2 rows) matching Screenshot 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {CASE_SUMMARY.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 sm:gap-5 rounded-[14px] sm:rounded-[16px] bg-white border border-[#EAF2FA] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] transition-all duration-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] hover:-translate-y-0.5"
                  >
                    {/* Circular Blue Icon Badge */}
                    <div className="flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                      <Icon className="h-6 w-6 stroke-[2]" />
                    </div>

                    {/* Text Details */}
                    <div className="flex flex-col gap-1.5 pt-0.5">
                      <span className="text-[12px] sm:text-[13px] font-bold uppercase tracking-wider text-[#0F172A]">
                        {card.title}
                      </span>
                      <p className="text-[13.5px] sm:text-[14px] leading-[21px] text-[#64748B]">
                        {card.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            2. The Context Section (matching Screenshot 4)
            Background: /images/insights/patient-context-bg.jpg
            Left: Deep dark gradient overlay with heading & narrative text
           ========================================================================= */}
        <section
          id="the-context"
          className="relative w-full overflow-hidden bg-[#050B14] min-h-[340px] sm:h-[350px] lg:h-[360px] flex items-center text-white"
        >
          {/* Base Background Image: Full span with right-aligned focus */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none overflow-hidden">
            <img
              src="/images/insights/patient-context-bg.jpg"
              alt="Patient experience context - Doctor with elderly patients"
              className="h-full w-full object-cover object-[85%_center] lg:object-right"
            />
          </div>

          {/* Left Solid Dark Base & Progressive Gradient Overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #050B14 0%, #050B14 36%, rgba(5, 11, 20, 0.95) 48%, rgba(5, 11, 20, 0.45) 66%, rgba(5, 11, 20, 0) 84%)",
            }}
          />

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-10 sm:py-12 lg:py-0">
            <div className="max-w-[620px] lg:max-w-[660px]">
              {/* Header row with horizontal divider line matching Screenshot 4 */}
              <div className="flex items-center gap-6 mb-5 sm:mb-6">
                <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The Context
                </h2>
                <div className="flex-1 h-[1px] bg-[#CEDDF1]/30" />
              </div>

              {/* Paragraph 1 matching Screenshot 4 */}
              <p className="text-[13px] sm:text-[14px] lg:text-[14.5px] leading-[23px] sm:leading-[25px] text-white/85">
                Every patient interaction — at the front desk, on a call, in a clinic or at discharge — is a chance to show that the organization understands their needs. When relevant context is available in the moment, interactions become more personal, clearer and more reassuring.
              </p>

              {/* Paragraph 2 matching Screenshot 4 */}
              <p className="mt-4 sm:mt-5 text-[13px] sm:text-[14px] lg:text-[14.5px] leading-[23px] sm:leading-[25px] text-white/85">
                Patient Experience Copilot helps teams understand needs, access relevant information and support more personalized interactions — as a lasting capability, not a short-term campaign.
              </p>
            </div>
          </div>
        </section>

        
        {/* =========================================================================
            3. What the solution does Section (matching Screenshot 1)
            Background: rgba(245, 250, 254, 1)
           ========================================================================= */}
        <section
          id="what-the-solution-does"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-12 justify-between">
              {/* Left Column: Solution Assistant Card matching Screenshot 1 (676px x 308px, Radius 12px, Shadow 4px 4px 24px rgba(0,0,0,0.25)) */}
              <div className="w-full lg:w-[676px] shrink-0 flex justify-center lg:justify-start">
                <div
                  style={{
                    boxShadow: "4px 4px 24px 0px rgba(0, 0, 0, 0.25)",
                    borderRadius: "12px",
                  }}
                  className="w-full max-w-[676px] h-[240px] sm:h-[280px] lg:h-[308px] overflow-hidden rounded-[12px] bg-white border border-[#E2EDF8]"
                >
                  <img
                    src="/images/insights/patient-solution-assistant.jpg"
                    alt="Patient Experience Copilot Conversation Assistant"
                    className="w-full h-full object-cover block select-none"
                  />
                </div>
              </div>

              {/* Right Column: Narrative Details */}
              <div className="flex-1 max-w-[560px] flex flex-col justify-center">
                {/* Header row with horizontal line matching Screenshot 1 */}
                <div className="flex items-center gap-6 mb-6 sm:mb-7">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    What the solution does
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                <p className="text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-[#64748B]">
                  Front-line and care teams get intelligent support during patient interactions. The Copilot helps surface relevant information — care status, common questions, approved explanations and institutional guidance — so staff can respond with clarity and empathy.
                </p>

                <p className="mt-5 sm:mt-6 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-[#64748B]">
                  Privacy and access controls ensure only authorised information is used. The Copilot supports people and processes; it does not replace clinical judgement or the human relationship at the centre of care.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            4. The challenge Section (matching Screenshot 3)
            Background: #FFFFFF (White)
            Left: The challenge heading + narrative paragraph
            Right: 2 Numbered Problem Cards (01, 02)
           ========================================================================= */}
        <section
          id="the-challenge"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Left Column: Heading + Narrative */}
              <div className="lg:col-span-5">
                <div className="flex items-center gap-6 mb-6 sm:mb-7">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    The challenge
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                <p className="text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  Patient interactions involve many touchpoints and information across systems. The challenge is to make relevant patient context easy to access in the moment so teams can have more informed and personal conversations.
                </p>
              </div>

              {/* Right Column: 2 Numbered Problem Cards */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                {/* Card 01 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#00D2EE] tracking-tight leading-none">
                      01
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Scattered information
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      <span className="block">Patient details, care plans and preferences</span>
                      <span className="block">are spread across systems, making it</span>
                      <span className="block">time-consuming to get a complete view.</span>
                    </p>
                  </div>
                </div>

                {/* Card 02 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#00D2EE] tracking-tight leading-none">
                      02
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Inconsistent experience
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      <span className="block">Without instant access to the right</span>
                      <span className="block">information, patient interactions can feel</span>
                      <span className="block">generic and lead to repeated questions.</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            5. Who benefits Section (matching Screenshot 4)
            Background: /images/insights/patient-experience-hero.jpg
            Table: Fill 1,280px, Radius 12px, rgba(9, 21, 40, 0.6), blur 20px
            Header: Height 49px, rgba(14, 29, 53, 1), borderBottom rgba(26, 46, 76, 1)
           ========================================================================= */}
        <section
          id="who-benefits"
          className="relative w-full overflow-hidden bg-[#020B18] py-16 sm:py-20 lg:py-24 text-white"
        >
          {/* Base Background Image: Patient Experience Hero Photo */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/patient-experience-hero.jpg"
              alt="Who Benefits Background"
              className="h-full w-full object-cover object-center"
            />
          </div>

          {/* Dark Glass Overlay matching Screenshot 4 */}
          <div className="absolute inset-0 bg-[#020B18]/60 backdrop-blur-[2px] pointer-events-none" />

          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with gradient text and horizontal divider */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Who benefits
              </h2>
              <div className="flex-1 h-[1px] bg-[#CEDDF1]/40" />
            </div>

            {/* Subtitle matching Screenshot 4 */}
            <p className="text-[13.5px] sm:text-[14.5px] leading-[22px] text-white/80 mb-8 sm:mb-10 max-w-[840px]">
              Five roles gain when every interaction is better informed — continuously, not only on a pilot day.
            </p>

            {/* Roles Table Container matching exact Figma specs */}
            <div
              style={{
                backgroundColor: "rgba(9, 21, 40, 0.28)",
                borderColor: "rgba(26, 46, 76, 1)",
                borderWidth: "1px",
                borderStyle: "solid",
                borderRadius: "12px",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
              }}
              className="w-full max-w-[1280px] mx-auto rounded-[12px] overflow-hidden"
            >
              {/* Table Header: Height 49px, rgba(14, 29, 53, 1), borderBottom rgba(26, 46, 76, 1) */}
              <div
                style={{
                  backgroundColor: "rgba(14, 29, 53, 0.65)",
                  borderBottomColor: "rgba(26, 46, 76, 1)",
                }}
                className="grid grid-cols-12 px-6 h-[49px] items-center border-b text-[11px] sm:text-[12px] font-bold tracking-[1.5px] uppercase text-white/90"
              >
                <div className="col-span-12 sm:col-span-4 lg:col-span-4">Role</div>
                <div className="hidden sm:block sm:col-span-8 lg:col-span-8">What improves for them</div>
              </div>

              {/* Table Rows with divider matching rgba(26, 46, 76, 1) */}
              <div className="divide-y" style={{ borderColor: "rgba(26, 46, 76, 1)" }}>
                {WHO_BENEFITS_DATA.map((item) => (
                  <div
                    key={item.role}
                    style={{ borderColor: "rgba(26, 46, 76, 1)" }}
                    className="grid grid-cols-12 px-6 py-4 sm:py-5 items-start gap-2 sm:gap-6 hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="col-span-12 sm:col-span-4 lg:col-span-4 text-[14px] sm:text-[15px] font-bold text-white leading-snug">
                      {item.role}
                    </div>
                    <div className="col-span-12 sm:col-span-8 lg:col-span-8 text-[13px] sm:text-[13.5px] leading-[22px] text-white/80">
                      <span className="sm:hidden text-[11px] uppercase tracking-wider text-slate-400 font-semibold block mb-1">
                        What improves:
                      </span>
                      {item.improves}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            6. Results organizations typically see Section (matching Screenshot 5)
            Background: rgba(245, 250, 254, 1)
            6 Metric Cards (3 cols x 2 rows)
           ========================================================================= */}
        <section
          id="results-organizations-typically-see"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Results organizations typically see
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* 6 Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {RESULTS_DATA.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 sm:gap-5 rounded-[14px] sm:rounded-[16px] bg-white border border-[#EAF2FA] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] transition-all duration-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] hover:-translate-y-0.5"
                  >
                    {/* Circular Blue Icon Badge */}
                    <div className="flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                      <Icon className="h-6 w-6 stroke-[2]" />
                    </div>

                    {/* Text Details */}
                    <div className="flex flex-col gap-1.5 pt-0.5">
                      <span className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {card.subtitle}
                      </span>
                      <h3 className="text-[15.5px] sm:text-[16.5px] font-bold text-[#0F172A] leading-snug">
                        {card.title}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>


        {/* =========================================================================
            7. How it works in practice Section (matching Screenshot 1)
            Background: #FFFFFF (White)
            5-Step Process Flow: Understand -> Get insights -> Respond -> Follow up -> Improve
            Bottom: Head of Operations Quote Box
           ========================================================================= */}
        <section
          id="how-it-works-in-practice"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                How it works in practice
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* Subtitle matching Screenshot 1 */}
            <p className="text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[23px] text-[#64748B] mb-12 sm:mb-16 max-w-[1040px]">
              From a patient question to a clear, personalized response — helping teams access the right information, reduce repeated queries and create a better experience at every touchpoint.
            </p>

            {/* 5-Step Process Flow with Connectors */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-2">
              {PROCESS_STEPS_PATIENT.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.id}>
                    {/* Step Item */}
                    <div className="flex flex-col items-center text-center max-w-[210px] w-full group">
                      {/* Colored Circle Badge */}
                      <div
                        className={`flex h-16 w-16 sm:h-18 sm:w-18 items-center justify-center rounded-full ${step.bgGradient} ${step.glow} text-white transition-transform duration-300 group-hover:scale-110 mb-4 sm:mb-5`}
                      >
                        <Icon className="h-7 w-7 sm:h-8 sm:w-8 stroke-[2.2]" />
                      </div>

                      {/* Step Title */}
                      <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] mb-2">
                        {step.title}
                      </h3>

                      {/* Step Description */}
                      <p className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {step.desc}
                      </p>
                    </div>

                    {/* Connecting Arrow between steps (hidden on last step) */}
                    {idx < PROCESS_STEPS_PATIENT.length - 1 && (
                      <div className="hidden lg:flex items-center justify-center shrink-0 px-2 text-[#00D2EE]">
                        <ArrowRight className="h-6 w-6 stroke-[2.5]" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Quote Callout Box matching Screenshot 1 */}
            <div className="mt-12 sm:mt-16 w-full rounded-[14px] sm:rounded-[18px] bg-[#F5FAFE] border border-[#E2EDF8] py-6 sm:py-7 px-6 sm:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 shadow-[0_2px_12px_rgba(15,23,42,0.02)]">
              <div className="flex items-start gap-3.5">
                <span className="text-[#00D2EE] text-[32px] sm:text-[40px] font-serif leading-none select-none -mt-1">
                  &ldquo;
                </span>
                <p className="text-[14.5px] sm:text-[15.5px] font-medium text-[#1E293B] leading-[25px] sm:leading-[27px]">
                  <span className="block">
                    &ldquo;We did not need more meetings about flow. We needed to see where work was stuck and cut the
                  </span>
                  <span className="block mt-0.5">
                    admin that did not need a person. The Copilot gave us both — and the pathway moved faster.&rdquo;
                  </span>
                </p>
              </div>

              <div className="shrink-0 lg:border-l-2 lg:border-[#00D2EE] lg:pl-6 pt-4 lg:pt-0 border-t lg:border-t-0 border-[#E2EDF8]">
                <span className="block text-[14px] sm:text-[14.5px] font-bold text-[#0F172A] leading-tight">
                  Head of Operations
                </span>
                <span className="block text-[12px] sm:text-[12.5px] text-[#64748B] mt-1">
                  Healthcare Organization
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            8. CTA Banner Section (matching Screenshot 2 & 3)
            Background: /images/insights/patient-cta-bg.jpg
            Left: Solid Dark (#050B14) + Progressive Blur & Gradient Overlay
            Right: Exposed Nurse & Elderly Patient in Bed
            CTA: Request a Consultation ->
           ========================================================================= */}
        <section
          id="cta-banner"
          className="relative w-full overflow-hidden bg-[#050B14] flex items-center min-h-[340px] lg:h-[340px] text-white"
        >
          {/* Base Background Image: Full span with right-aligned focus so left edge NEVER shows */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none overflow-hidden">
            <img
              src="/images/insights/patient-cta-bg.jpg"
              alt="Turn patient experience into stronger, more connected care"
              className="h-full w-full object-cover object-[80%_center] lg:object-right"
            />
          </div>

          {/* Linear Gradient Overlay: 100% solid #050B14 on the left, smoothly fading out to the right */}
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #050B14 0%, #050B14 36%, rgba(5, 11, 20, 0.95) 48%, rgba(5, 11, 20, 0.45) 66%, rgba(5, 11, 20, 0) 84%)",
            }}
          />

          {/* Figma Progressive Blur Layer: 45.73% to 64.69%, start blur 14, end blur 0 */}
          <div
            className="absolute inset-y-0 left-0 w-[68%] pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(5, 11, 20, 0.8) 0%, rgba(5, 11, 20, 0.5) 45.73%, rgba(5, 11, 20, 0) 64.69%)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              maskImage: "linear-gradient(to right, black 45.73%, transparent 64.69%)",
              WebkitMaskImage: "linear-gradient(to right, black 45.73%, transparent 64.69%)",
            }}
          />

          {/* Content Container (Max width 1440px matching layout) */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-10 sm:py-12 lg:py-0 flex flex-col justify-center">
            <div className="max-w-[800px] xl:max-w-[880px] w-full">
              {/* Heading matching Screenshot 2 */}
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight leading-[1.2]">
                <span className="bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                  Turn patient experience into stronger,
                </span>
                <br className="hidden sm:inline" />
                <span className="bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                  {" "}more connected care.
                </span>
              </h2>

              {/* Subtitle matching Screenshot 2 */}
              <p className="mt-4 sm:mt-5 text-[14px] sm:text-[15px] lg:text-[15.5px] leading-[24px] sm:leading-[26px] text-white/85 max-w-[620px]">
                See how Simpel Patient Experience Copilot can help your teams create more informed, personalized interactions.
              </p>

              {/* CTA Button matching Screenshot 2 */}
              <div className="mt-7 sm:mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-[14px] sm:text-[15px] font-semibold text-[#0F172A] shadow-md transition-all duration-200 hover:bg-slate-100 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Request a Consultation
                  <ArrowRight className="h-4 w-4 stroke-[2.5] text-[#0F172A]" />
                </Link>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default PatientExperienceCopilot;

